import { create } from 'zustand';
import type { Action, Addons, BotDifficulty, VocationId } from '@estandarte/engine';
import type { ClientMessage, LobbyState, ServerMessage } from '@estandarte/protocol';
import { readJson, SESSION_KEY, writeJson } from '../lib/storage';
import { errorText } from '../theme';
import { useGame } from './game';

export const SERVER_URL: string = import.meta.env.VITE_SERVER_URL ?? 'ws://localhost:8787';

interface SessionEntry {
  token: string;
  code: string;
  name: string;
  at: number;
}

const TAB_TOKEN_KEY = 'estandarte.tab-token';
const ALIVE_PREFIX = 'estandarte.alive.';
const ALIVE_MS = 6000;

function sessions(): SessionEntry[] {
  return readJson<{ sessions: SessionEntry[] }>(SESSION_KEY)?.sessions ?? [];
}

function saveSession(entry: SessionEntry) {
  const list = sessions().filter((s) => s.token !== entry.token && Date.now() - s.at < 24 * 3600_000);
  list.unshift(entry);
  writeJson(SESSION_KEY, { sessions: list.slice(0, 10) });
}

function dropSession(token: string) {
  writeJson(SESSION_KEY, { sessions: sessions().filter((s) => s.token !== token) });
  try {
    if (window.sessionStorage.getItem(TAB_TOKEN_KEY) === token) window.sessionStorage.removeItem(TAB_TOKEN_KEY);
    window.localStorage.removeItem(ALIVE_PREFIX + token);
  } catch {
    /* sem armazenamento */
  }
}

function tabToken(): string | null {
  try {
    return window.sessionStorage.getItem(TAB_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setTabToken(token: string) {
  try {
    window.sessionStorage.setItem(TAB_TOKEN_KEY, token);
  } catch {
    /* sem armazenamento */
  }
}

function isAliveElsewhere(token: string): boolean {
  const at = readJson<number>(ALIVE_PREFIX + token);
  return !!at && Date.now() - at < ALIVE_MS;
}

/** Sessão salva que nenhuma outra aba aberta está usando (para retomar depois de fechar a aba). */
export function resumableSession(): SessionEntry | null {
  return sessions().find((s) => !isAliveElsewhere(s.token) && Date.now() - s.at < 6 * 3600_000) ?? null;
}

interface OnlineStore {
  status: 'idle' | 'connecting' | 'open' | 'closed';
  lobby: LobbyState | null;
  token: string | null;
  code: string | null;
  mySeat: string | null;
  host: boolean;
  error: string | null;
  create(name: string, password: string): void;
  join(code: string, name: string, password: string): void;
  resume(token: string): void;
  resumeTab(): boolean;
  setSeat(color?: string, vocation?: VocationId): void;
  setAddons(addons: Addons): void;
  setEra(era: string | null): void;
  addBot(difficulty?: BotDifficulty): void;
  editBot(seat: string, patch: { color?: string; vocation?: VocationId; difficulty?: BotDifficulty }): void;
  removeSeat(seat: string): void;
  start(): void;
  leave(): void;
  clearError(): void;
}

let socket: WebSocket | null = null;
let pendingFirst: ClientMessage | null = null;
let heartbeat: ReturnType<typeof setInterval> | null = null;
let retry: ReturnType<typeof setTimeout> | null = null;
let retries = 0;
let leaving = false;

export const useOnline = create<OnlineStore>((set, get) => {
  function send(msg: ClientMessage) {
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
  }

  function startHeartbeat() {
    stopHeartbeat();
    const beat = () => {
      const token = get().token;
      if (token) writeJson(ALIVE_PREFIX + token, Date.now());
      send({ t: 'ping' });
    };
    beat();
    heartbeat = setInterval(beat, 2000);
  }

  // Ao fechar a aba, libera a sessão na hora para que outra aba possa retomá-la.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      const token = get().token;
      if (!token) return;
      try {
        window.localStorage.removeItem(ALIVE_PREFIX + token);
      } catch {
        /* sem armazenamento */
      }
    });
  }

  function stopHeartbeat() {
    if (heartbeat) clearInterval(heartbeat);
    heartbeat = null;
  }

  function connect(first: ClientMessage) {
    leaving = false;
    if (retry) clearTimeout(retry);
    retry = null;
    if (socket && socket.readyState === WebSocket.OPEN) {
      send(first);
      return;
    }
    socket?.close();
    pendingFirst = first;
    set({ status: 'connecting', error: null });
    let ws: WebSocket;
    try {
      ws = new WebSocket(SERVER_URL);
    } catch {
      set({ status: 'closed', error: errorText('connection') });
      return;
    }
    socket = ws;
    ws.onopen = () => {
      retries = 0;
      set({ status: 'open' });
      if (pendingFirst) ws.send(JSON.stringify(pendingFirst));
      pendingFirst = null;
      startHeartbeat();
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      handle(msg);
    };
    ws.onclose = () => {
      if (socket !== ws) return;
      socket = null;
      stopHeartbeat();
      set({ status: 'closed' });
      const token = get().token;
      // Caiu no meio da sala: tenta voltar com o token.
      if (!leaving && token && retries < 8) {
        retries++;
        set({ error: 'Conexão perdida. Tentando reconectar…' });
        retry = setTimeout(() => connect({ t: 'resume', token }), Math.min(8000, 800 * 2 ** retries));
      } else if (!leaving && !token) {
        set({ error: errorText('connection') });
      }
    };
  }

  function handle(msg: ServerMessage) {
    switch (msg.t) {
      case 'welcome': {
        set({ token: msg.token, code: msg.code, mySeat: msg.seat, host: msg.host, error: null });
        setTabToken(msg.token);
        const name = get().lobby?.seats.find((s) => s.id === msg.seat)?.name ?? '';
        saveSession({ token: msg.token, code: msg.code, name, at: Date.now() });
        break;
      }
      case 'lobby': {
        const me = msg.lobby.seats.find((s) => s.id === get().mySeat);
        set({ lobby: msg.lobby, host: !!me?.isHost, error: null });
        const token = get().token;
        if (token && me) saveSession({ token, code: msg.lobby.code, name: me.name, at: Date.now() });
        if (useGame.getState().screen !== 'lobby') useGame.getState().go('lobby');
        break;
      }
      case 'game': {
        const game = useGame.getState();
        game.setOnlineSender((action: Action) => send({ t: 'action', action }));
        game.receiveOnline(msg.view, msg.me, msg.events, msg.seats, msg.host);
        const token = get().token;
        if (token) saveSession({ token, code: get().code ?? '', name: msg.view.players[msg.me]?.name ?? '', at: Date.now() });
        set({ error: null });
        break;
      }
      case 'error': {
        if (msg.code === 'bad_token') {
          const token = get().token;
          if (token) dropSession(token);
          leaving = true;
          set({ token: null, lobby: null, mySeat: null, code: null });
          // A sala não existe mais (servidor reiniciou ou limpou): volta ao início.
          if (useGame.getState().mode === 'online') useGame.getState().leaveGame();
          if (useGame.getState().screen === 'lobby') useGame.getState().go('home');
        }
        const text = errorText(msg.code);
        set({ error: text });
        if (useGame.getState().mode === 'online') useGame.getState().setError(text);
        break;
      }
      case 'kicked':
      case 'left': {
        const token = get().token;
        if (token) dropSession(token);
        leaving = true;
        socket?.close();
        set({ token: null, lobby: null, mySeat: null, code: null, status: 'idle', error: msg.t === 'kicked' ? errorText('kicked') : null });
        if (useGame.getState().mode === 'online') useGame.getState().leaveGame();
        useGame.getState().go('home');
        break;
      }
      case 'replaced': {
        // A mesma sessão foi aberta em outra aba: esta para de tentar reconectar.
        leaving = true;
        socket?.close();
        set({ token: null, lobby: null, mySeat: null, code: null, status: 'idle', error: 'Sua sessão foi aberta em outra aba.' });
        if (useGame.getState().mode === 'online') useGame.getState().leaveGame();
        useGame.getState().go('home');
        break;
      }
      case 'pong':
        break;
    }
  }

  return {
    status: 'idle',
    lobby: null,
    token: null,
    code: null,
    mySeat: null,
    host: false,
    error: null,
    create: (name, password) => connect({ t: 'create', name, password: password || undefined }),
    join: (code, name, password) => connect({ t: 'join', code, name, password: password || undefined }),
    resume(token) {
      set({ token });
      connect({ t: 'resume', token });
    },
    resumeTab() {
      const token = tabToken();
      if (!token) return false;
      get().resume(token);
      return true;
    },
    setSeat: (color, vocation) => send({ t: 'seat', color, vocation }),
    setAddons: (addons) => send({ t: 'addons', addons }),
    setEra: (era) => send({ t: 'era', era }),
    addBot: (difficulty) => send({ t: 'addBot', difficulty }),
    editBot: (seat, patch) => send({ t: 'botSeat', seat, ...patch }),
    removeSeat: (seat) => send({ t: 'removeSeat', seat }),
    start: () => send({ t: 'start' }),
    leave() {
      send({ t: 'leave' });
      const token = get().token;
      if (token) dropSession(token);
      leaving = true;
      setTimeout(() => socket?.close(), 100);
      set({ token: null, lobby: null, mySeat: null, code: null, status: 'idle' });
      if (useGame.getState().mode === 'online') useGame.getState().leaveGame();
      useGame.getState().go('home');
    },
    clearError: () => set({ error: null }),
  };
});
